import { useCallback, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';

type UseDiscardConfirmationProps = {
    // biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration
    readonly formMethods: UseFormReturn<any>;
    readonly hasUnsavedChanges?: boolean;
    readonly isEditing: boolean;
    readonly onClose: () => void;
    readonly setIsEditing: (editing: boolean) => void;
};

type UseDiscardConfirmationReturn = {
    readonly handleCancel: () => void;
    readonly handleCancelDiscard: () => void;
    readonly handleClose: () => void;
    readonly handleConfirmDiscard: () => void;
    readonly isDiscardDialogOpen: boolean;
};

export const useDiscardConfirmation = ({
    formMethods,
    hasUnsavedChanges,
    isEditing,
    onClose,
    setIsEditing,
}: UseDiscardConfirmationProps): UseDiscardConfirmationReturn => {
    const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
    const [pendingAction, setPendingAction] = useState<'cancel' | 'close' | null>(null);

    const handleCancel = useCallback(() => {
        if (hasUnsavedChanges ?? formMethods.formState.isDirty) {
            setPendingAction('cancel');
            setIsDiscardDialogOpen(true);
        } else {
            formMethods.reset();
            setIsEditing(false);
        }
    }, [formMethods, hasUnsavedChanges, setIsEditing]);

    const handleClose = useCallback(() => {
        if (isEditing && (hasUnsavedChanges ?? formMethods.formState.isDirty)) {
            setPendingAction('close');
            setIsDiscardDialogOpen(true);
        } else {
            onClose();
        }
    }, [isEditing, hasUnsavedChanges, formMethods.formState.isDirty, onClose]);

    const handleConfirmDiscard = useCallback(() => {
        if (pendingAction === 'cancel') {
            formMethods.reset();
            setIsEditing(false);
        } else if (pendingAction === 'close') {
            formMethods.reset();
            setIsEditing(false);
            onClose();
        }
        setIsDiscardDialogOpen(false);
        setPendingAction(null);
    }, [pendingAction, formMethods, setIsEditing, onClose]);

    const handleCancelDiscard = useCallback(() => {
        setIsDiscardDialogOpen(false);
        setPendingAction(null);
    }, []);

    return {
        handleCancel,
        handleCancelDiscard,
        handleClose,
        handleConfirmDiscard,
        isDiscardDialogOpen,
    };
};
